import { TypographyGroupValue } from '@makyoapp/makyo-common'
import { BarChart, RawSpacingInput } from '@makyoapp/makyo-tw-ui'

import { IDBTable } from '@/core/db'
import { CoreComponentInfos, CoreComponentTableData } from '@/core/models/app-components/types'
import { IRow } from '@/core/models/tables/row'
import { useAppBuilderContext } from '@/context/AppBuilderContext'
import { ComponentSettingKey } from '@/shared/builder'
import { getSafely } from '@/shared/safely'
import { CompOverlayDevmode } from '@/components/__project__/shared/CompOverlayDevmode'

import { CompHeadline } from '../../../shared/CompHeadline'

interface ConfigKeys {
  xAxisColumn: string
  valueColumns: string[]
}

const getColumnLabel = (table: IDBTable | undefined, key: string, firstRow: IRow): string => {
  const column = firstRow[key]
  if (table && column && column instanceof Object) return `${table?.columnsMap.get(key)?._Name}`
  return key
}

const formatDataForBarChart = (table: IDBTable | undefined, rawData: IRow[], config: ConfigKeys, maxItems: number) => {
  if (!rawData?.length || !config?.valueColumns?.length) {
    return { data: [], keys: [] }
  }

  const valueColumns = Array.isArray(config.valueColumns) ? config.valueColumns : []

  const keys = valueColumns?.map((key) => ({
    originalKey: key,
    label: getColumnLabel(table, key, rawData[0]),
  }))

  const formattedData = rawData.slice(0, maxItems).map((item) => {
    const value = item[config.xAxisColumn]?.value?.toString() || ''

    const formattedItem: any = {
      key: value.length > 15 ? value.substring(0, 15) + '...' : value,
    }

    keys.forEach(({ originalKey, label }) => {
      const value = item[originalKey]?.value
      let numericValue = 0
      if (typeof value === 'string') {
        numericValue = parseFloat(value)
      } else if (typeof value === 'number') {
        numericValue = value
      }
      formattedItem[label] = isNaN(numericValue) ? 0 : numericValue
    })

    return formattedItem
  })

  return {
    data: formattedData,
    keys: keys.map((k) => k.label),
  }
}

export interface CompChartBarWrapperCoreProps {
  infos: CoreComponentInfos
  tableData: CoreComponentTableData
}

/**
 * CompChartBarWrapperCore
 *
 */
export const CompChartBarWrapperCore = ({
  infos: { component, settings, containerClient, detailTableId, detailRowIndex, currentParentBg },
  tableData: { rows = [], dbTable },
}: CompChartBarWrapperCoreProps) => {
  const { isDevelopmentMode, openComponentSettings } = useAppBuilderContext()

  // ========================= data =========================
  const xAxisColumn = getSafely(settings, [ComponentSettingKey.BarChartDataXAxisColumn], '')
  const valueColumns = getSafely(settings, [ComponentSettingKey.BarChartDataValueColumns], [])
  const itemsPerPage = getSafely(settings, [ComponentSettingKey.BarChartDataItemsPerPage], [10])[0]
  const maxItems = itemsPerPage > 50 ? 50 : itemsPerPage

  // format data
  const config: ConfigKeys = { xAxisColumn, valueColumns }
  const chartData = formatDataForBarChart(dbTable, rows, config, maxItems)

  // ========================= design =========================

  // base
  const width = getSafely(settings, [ComponentSettingKey.BarChartDesignWidth], ['100%'])[0]
  const alignment = settings[ComponentSettingKey.BarChartDesignAlignment] ?? 'parent'
  const margin = settings[ComponentSettingKey.BarChartDesignCompMargin]

  const colorSequence = getSafely(settings, [ComponentSettingKey.BarChartDesignColorSequence], 'random') as
    | 'sequential'
    | 'random'

  // const abbreviateLabel = getSafely(settings, [ComponentSettingKey.BarChartDesignAbbrNumber], true)
  const showLegend = getSafely(settings, [ComponentSettingKey.BarChartDesignLegend], true)
  const showGrid = getSafely(settings, [ComponentSettingKey.BarChartDesignShowGrid], true)
  const showAxis = getSafely(settings, [ComponentSettingKey.BarChartDesignShowAxis], false)
  const orientation = getSafely(settings, [ComponentSettingKey.BarChartDesignHorizontal], 'vertical') as
    | 'horizontal'
    | 'vertical'

  const barStyle = getSafely(settings, [ComponentSettingKey.BarChartDesignStyle], 'grouped')
  const typoSetting = getSafely(settings, [ComponentSettingKey.BarChartDesignTypography], {}) as TypographyGroupValue

  // ========================= extra =========================
  // comp title (headline)
  const headline = settings[ComponentSettingKey.BarChartDataHeading] ?? []
  const headlineAlign = getSafely(settings, [ComponentSettingKey.BarChartDesignHeadingAlignment], 'start')
  const headlineTypography = typoSetting[ComponentSettingKey.BarChartDesignHeadingTypography]
  const headlineMargin = settings[ComponentSettingKey.BarChartDesignHeadingMargin] as RawSpacingInput

  // ========================= render =========================
  const handleClick = () => {
    openComponentSettings(component)
    if (isDevelopmentMode) return
  }

  const barChartComponent = (
    <BarChart
      id={component._id}
      width={width}
      alignment={alignment}
      margin={margin}
      backgroundColor={currentParentBg}
      before={
        <>
          <CompHeadline
            containerClient={containerClient}
            detailTableId={detailTableId}
            detailRowIndex={detailRowIndex}
            headline={headline}
            compTitleAlign={headlineAlign}
            headlineMargin={headlineMargin}
            headlineTypo={headlineTypography}
            backgroundColor={currentParentBg}
            colorType='adaptive'
          />
        </>
      }
      data={chartData.data}
      dataKeys={chartData.keys}
      xAxisKey={config.xAxisColumn}
      orientation={orientation}
      showLegend={showLegend}
      showTooltip={true}
      showGrid={showGrid}
      showAxis={showAxis}
      stacked={barStyle === 'stacked'}
      sequenceType={colorSequence}
    />
  )

  if (isDevelopmentMode) {
    return (
      <div className='relative flex flex-col w-full border border-dashed' style={{ borderColor: '#333' }}>
        {barChartComponent}
        <CompOverlayDevmode handleClick={handleClick} />
      </div>
    )
  }

  return barChartComponent
}
